export function Field({ id, label, hint, error, required, children }) {
  return (
    <div className="mb-5">
      <label htmlFor={id} className="field-label">{label}{required && <span className="text-[#B81B25]"> *</span>}{!required && <span className="font-normal text-neutral-500"> (optional)</span>}</label>
      {children}
      {hint && <p id={`${id}-hint`} className="field-hint">{hint}</p>}
      {error && <p id={`${id}-error`} className="field-error" role="alert">{error}</p>}
    </div>
  );
}

const aria = (id, hint, error) => ({ "aria-invalid": !!error, "aria-describedby": [hint && `${id}-hint`, error && `${id}-error`].filter(Boolean).join(" ") || undefined });

export function TextInput({ id, label, value, onChange, hint, error, required, type = "text", ...rest }) {
  return (
    <Field id={id} label={label} hint={hint} error={error} required={required}>
      <input id={id} type={type} className="input" value={value ?? ""} onChange={(e) => onChange(e.target.value)} {...aria(id, hint, error)} data-testid={`field-${id}`} {...rest} />
    </Field>
  );
}

export function TextArea({ id, label, value, onChange, hint, error, required, rows = 3 }) {
  return (
    <Field id={id} label={label} hint={hint} error={error} required={required}>
      <textarea id={id} rows={rows} maxLength={2000} className="input min-h-[110px]" value={value ?? ""} onChange={(e) => onChange(e.target.value)} {...aria(id, hint, error)} data-testid={`field-${id}`} />
    </Field>
  );
}

export function SelectInput({ id, label, value, onChange, options, hint, error, required }) {
  return (
    <Field id={id} label={label} hint={hint} error={error} required={required}>
      <select id={id} className="input" value={value ?? ""} onChange={(e) => onChange(e.target.value)} {...aria(id, hint, error)} data-testid={`field-${id}`}>
        <option value="">Choose one…</option>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </Field>
  );
}

export function Choice({ id, label, value, onChange, options, error, required, hint }) {
  return (
    <fieldset className="mb-5" id={id} {...aria(id, hint, error)} tabIndex={-1}>
      <legend className="field-label">{label}{required ? <span className="text-[#B81B25]"> *</span> : <span className="font-normal text-neutral-500"> (optional)</span>}</legend>
      <div className="grid gap-2 sm:grid-cols-2">
        {options.map(([v, l]) => (
          <label key={v} className={`flex min-h-[52px] cursor-pointer items-center gap-3 rounded-md border-2 px-4 py-2 text-base ${value === v ? "border-[#D9232E] bg-red-50 font-bold" : "border-neutral-300 bg-white hover:border-neutral-500"}`}>
            <input type="radio" name={id} value={v} checked={value === v} onChange={() => onChange(v)} className="h-5 w-5 accent-[#D9232E]" data-testid={`choice-${id}-${v}`} />
            {l}
          </label>
        ))}
      </div>
      {hint && <p id={`${id}-hint`} className="field-hint">{hint}</p>}
      {error && <p id={`${id}-error`} className="field-error" role="alert">{error}</p>}
    </fieldset>
  );
}

export function Check({ id, label, checked, onChange, desc, error }) {
  return (
    <div className="mb-4">
      <label htmlFor={id} className={`flex cursor-pointer items-start gap-3 rounded-md border-2 p-4 ${checked ? "border-[#D9232E] bg-red-50" : "border-neutral-300 bg-white"}`}>
        <input id={id} type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-6 w-6 shrink-0 accent-[#D9232E]" {...aria(id, null, error)} data-testid={`check-${id}`} />
        <span><span className="text-base font-bold">{label}</span>{desc && <span className="mt-1 block text-sm text-neutral-700">{desc}</span>}</span>
      </label>
      {error && <p id={`${id}-error`} className="field-error" role="alert">{error}</p>}
    </div>
  );
}
